import {DateTime, Diagram, DiagramType, nominate} from "platformed-client/types";

// ICFR Architecture Diagram
// Demonstrates internal controls over financial reporting with IT security and monitoring controls
export const icfrArchitecture: Diagram = {
  diagram_id: nominate("DiagramId", "hardcoded-icfr-001"),
  name: "ICFR Architecture",
  description:
    "ICFR architecture demonstrating internal controls over financial reporting with IT security controls. Shows segregation of duties across ERP (SAP S/4HANA), consolidation (Oracle EPM), reconciliation (BlackLine), and supporting IT infrastructure including identity management (Azure AD), privileged access controls (CyberArk), backup/DR, vulnerability scanning, and security monitoring (Splunk/SIEM).",
  diagram_type: "ProcessFlow" as DiagramType,
  created_at: "2025-11-17T10:00:00Z" as DateTime,
  updated_at: "2025-11-17T10:00:00Z" as DateTime,
  owner: null,
  deleted: null,
  lanes_enabled: false,
  lanes: {},
  generation_state: {type: "NotStarted"},

  nodes: {
    hrPayroll: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-hr-payroll-team"),
      label: "HR / Payroll Team",
      description: "Maintains HR master data, payroll runs and reconciliations in Workday.",
      icon: "isoflow.user",
      position: [6, 10],
    },
    financeUsers: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-finance-users"),
      label: "Finance Users",
      description: "AP/AR/GL teams, preparers and reviewers operating SAP, EPM and BlackLine.",
      icon: "isoflow.user",
      position: [22, 4],
    },
    splunk: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-splunk-siem"),
      label: "Splunk / SIEM",
      description: "Central log aggregation, alerting, and dashboards for financial systems and security tooling.",
      icon: "isoflow.server",
      position: [29, 6],
    },
    workday: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-workday"),
      label: "Workday (HR / Payroll)",
      description: "HR master data and payroll processing; generates payroll journals for SAP.",
      icon: "isoflow.server",
      position: [10, 12],
    },
    azureAd: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-azure-ad"),
      label: "Azure AD (SSO/MFA)",
      description: "Central identity provider enforcing SSO and MFA for SAP, Workday, Concur, EPM, BlackLine and EUCs.",
      icon: "isoflow.server",
      position: [12, 6],
    },
    vpn: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-vpn"),
      label: "VPN",
      description: "Restricted remote access path for admins and key finance users; MFA enforced.",
      icon: "isoflow.server",
      position: [6, 6],
    },
    blackline: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      label: "BlackLine",
      description: "Account reconciliation and transaction matching across SAP and Oracle EPM.",
      icon: "isoflow.server",
      position: [22, 0],
    },
    oracleEpm: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Oracle EPM",
      description: "Consolidation, management reporting, and forecasting; receives GL data and EUC adjustments.",
      icon: "isoflow.server",
      position: [32, 12],
    },
    groupGl: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-group-gl-reporting"),
      label: "Group GL & Reporting",
      description: "Consolidated GL / reporting layer consuming SAP postings and feeding EPM disclosures.",
      icon: "fi.storage.015.book",
      position: [26, 0],
    },
    azureDevops: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-azure-devops"),
      label: "Azure DevOps Pipelines",
      description:
        "Release management pipelines for SAP extensions and related services; developers segregated from prod deployment.",
      icon: "isoflow.server",
      position: [34, 26],
    },
    bloombergFx: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-bloomberg-fx"),
      label: "Bloomberg FX Feed",
      description: "Daily FX rates feed with tolerance checks before posting to SAP.",
      icon: "isoflow.server",
      position: [11, 16],
    },
    internalAudit: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-internal-audit"),
      label: "Internal Audit / SOC",
      description: "Investigates alerts, reviews privileged activity, and monitors exception trends for ICFR.",
      icon: "isoflow.user",
      position: [35, 6],
    },
    jobScheduler: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-job-scheduler"),
      label: "Finance Job Scheduler",
      description: "Batch / interface scheduling for SAP and EPM (end-of-day, month-end jobs).",
      icon: "isoflow.server",
      position: [27, 23],
    },
    cyberark: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-cyberark-pam"),
      label: "CyberArk PAM",
      description: "Privileged access vaulting and session recording for SAP, EPM and database admins.",
      icon: "isoflow.server",
      position: [18, 25],
    },
    backupDr: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-backup-dr"),
      label: "Backup & DR Platform",
      description: "Nightly backups, weekly restore tests, and annual DR exercises for SAP and EPM.",
      icon: "isoflow.server",
      position: [32, 21],
    },
    qualys: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-qualys"),
      label: "Qualys Vulnerability Scanning",
      description: "External and internal vulnerability scans over SAP, EPM and supporting infrastructure.",
      icon: "isoflow.server",
      position: [38, 21],
    },
    sap: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "SAP S/4HANA (ERP)",
      description: "GL, AP, AR, Assets and core financial processing; central system of record for ICFR.",
      icon: "isoflow.server",
      position: [25, 15],
    },
    sharepointEucs: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-sharepoint-eucs"),
      label: "SharePoint EUCs",
      description: "Controlled repository for key spreadsheets (FX, allocations, depreciation models) feeding EPM.",
      icon: "isoflow.document",
      position: [38, 12],
    },
    sftpGateway: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-sftp-gateway"),
      label: "Secure SFTP Gateway",
      description: "Encrypted batch transfers for Workday → SAP and other financial interfaces.",
      icon: "isoflow.server",
      position: [16, 9],
    },
    concur: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-sap-concur"),
      label: "SAP Concur",
      description: "Employee expenses; sends approved AP expense feed into SAP.",
      icon: "isoflow.server",
      position: [22, 25],
    },
    keyVault: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-azure-key-vault"),
      label: "Azure Key Vault",
      description: "Central secrets store for interface accounts, SFTP keys, and pipeline credentials.",
      icon: "isoflow.server",
      position: [26, 26],
    },
    liquibase: {
      diagram_node_id: nominate("DiagramNodeId", "icfr-liquibase"),
      label: "Liquibase / DB Change Tooling",
      description: "Database change scripting, approvals, and deployment history for financial schemas.",
      icon: "isoflow.server",
      position: [38, 17],
    },
  },

  connections: {
    conn1: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-1"),
      source_node_id: nominate("DiagramNodeId", "icfr-internal-audit"),
      target_node_id: nominate("DiagramNodeId", "icfr-sharepoint-eucs"),
      label: "EUC inventory & periodic review",
      color: "green",
      anchors: [],
    },
    conn2: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-2"),
      source_node_id: nominate("DiagramNodeId", "icfr-internal-audit"),
      target_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      label: "Review high-risk reconciliations",
      color: "green",
      anchors: [],
    },
    conn3: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-3"),
      source_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "GL trial balance & subledger feeds",
      color: "blue",
      anchors: [],
    },
    conn4: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-4"),
      source_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      target_node_id: nominate("DiagramNodeId", "icfr-splunk-siem"),
      label: "Application & security logs",
      color: "purple",
      anchors: [],
    },
    conn5: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-5"),
      source_node_id: nominate("DiagramNodeId", "icfr-sftp-gateway"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Encrypted payroll journal feed",
      color: "blue",
      anchors: [{position: [18, 10]}, {position: [19, 10]}],
    },
    conn6: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-6"),
      source_node_id: nominate("DiagramNodeId", "icfr-finance-users"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Post journals / run subledger processes",
      color: "blue",
      anchors: [],
    },
    conn7: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-7"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-ad"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-concur"),
      label: "SSO / MFA",
      color: "green",
      anchors: [{position: [18, 12]}],
    },
    conn8: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-8"),
      source_node_id: nominate("DiagramNodeId", "icfr-sap-concur"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Approved expense / AP feed",
      color: "blue",
      anchors: [],
    },
    conn9: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-9"),
      source_node_id: nominate("DiagramNodeId", "icfr-cyberark-pam"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Privileged admin sessions (recorded)",
      color: "red",
      anchors: [],
    },
    conn10: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-10"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-key-vault"),
      target_node_id: nominate("DiagramNodeId", "icfr-sftp-gateway"),
      label: "Managed interface credentials",
      color: "purple",
      anchors: [],
    },
    conn11: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-11"),
      source_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      target_node_id: nominate("DiagramNodeId", "icfr-group-gl-reporting"),
      label: "Certified reconciliations",
      color: "green",
      anchors: [],
    },
    conn12: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-12"),
      source_node_id: nominate("DiagramNodeId", "icfr-finance-users"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Submit forecasts / review consolidations",
      color: "blue",
      anchors: [{position: [24, 7]}, {position: [27, 11]}],
    },
    conn13: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-13"),
      source_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      target_node_id: nominate("DiagramNodeId", "icfr-group-gl-reporting"),
      label: "Subledger postings",
      color: "blue",
      anchors: [{position: [26, 8]}],
    },
    conn14: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-14"),
      source_node_id: nominate("DiagramNodeId", "icfr-finance-users"),
      target_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      label: "Prepare & review reconciliations",
      color: "blue",
      anchors: [],
    },
    conn15: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-15"),
      source_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      target_node_id: nominate("DiagramNodeId", "icfr-splunk-siem"),
      label: "Application & security logs",
      color: "purple",
      anchors: [],
    },
    conn16: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-16"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-ad"),
      target_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      label: "SSO / MFA",
      color: "green",
      anchors: [],
    },
    conn17: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-17"),
      source_node_id: nominate("DiagramNodeId", "icfr-cyberark-pam"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Privileged admin sessions (recorded)",
      color: "red",
      anchors: [],
    },
    conn18: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-18"),
      source_node_id: nominate("DiagramNodeId", "icfr-group-gl-reporting"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Consolidated actuals / reporting feed",
      color: "blue",
      anchors: [],
    },
    conn19: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-19"),
      source_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      target_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      label: "Account balances & transactions",
      color: "blue",
      anchors: [{position: [24, 12]}],
    },
    conn20: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-20"),
      source_node_id: nominate("DiagramNodeId", "icfr-cyberark-pam"),
      target_node_id: nominate("DiagramNodeId", "icfr-splunk-siem"),
      label: "Privileged session logs",
      color: "purple",
      anchors: [],
    },
    conn21: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-21"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-devops"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Application releases (segregated pipelines)",
      color: "purple",
      anchors: [],
    },
    conn22: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-22"),
      source_node_id: nominate("DiagramNodeId", "icfr-workday"),
      target_node_id: nominate("DiagramNodeId", "icfr-sftp-gateway"),
      label: "Payroll export (CSV)",
      color: "blue",
      anchors: [],
    },
    conn23: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-23"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-key-vault"),
      target_node_id: nominate("DiagramNodeId", "icfr-azure-devops"),
      label: "Secrets for pipelines",
      color: "purple",
      anchors: [],
    },
    conn24: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-24"),
      source_node_id: nominate("DiagramNodeId", "icfr-job-scheduler"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Finance batch jobs & interfaces",
      color: "purple",
      anchors: [],
    },
    conn25: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-25"),
      source_node_id: nominate("DiagramNodeId", "icfr-backup-dr"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Nightly backups & DR protection",
      color: "purple",
      anchors: [],
    },
    conn26: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-26"),
      source_node_id: nominate("DiagramNodeId", "icfr-job-scheduler"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Consolidation and load jobs",
      color: "purple",
      anchors: [],
    },
    conn27: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-27"),
      source_node_id: nominate("DiagramNodeId", "icfr-qualys"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Vulnerability scan results",
      color: "purple",
      anchors: [],
    },
    conn28: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-28"),
      source_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      target_node_id: nominate("DiagramNodeId", "icfr-blackline"),
      label: "EPM balances for reconciliation",
      color: "blue",
      anchors: [],
    },
    conn29: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-29"),
      source_node_id: nominate("DiagramNodeId", "icfr-splunk-siem"),
      target_node_id: nominate("DiagramNodeId", "icfr-internal-audit"),
      label: "Alerts, dashboards & case queues",
      color: "green",
      anchors: [],
    },
    conn30: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-30"),
      source_node_id: nominate("DiagramNodeId", "icfr-backup-dr"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Nightly backups & DR protection",
      color: "purple",
      anchors: [],
    },
    conn31: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-31"),
      source_node_id: nominate("DiagramNodeId", "icfr-bloomberg-fx"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "Daily FX rates (validated)",
      color: "blue",
      anchors: [{position: [18, 16]}, {position: [20, 14]}],
    },
    conn32: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-32"),
      source_node_id: nominate("DiagramNodeId", "icfr-hr-payroll-team"),
      target_node_id: nominate("DiagramNodeId", "icfr-workday"),
      label: "Maintain HR & payroll data",
      color: "blue",
      anchors: [],
    },
    conn33: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-33"),
      source_node_id: nominate("DiagramNodeId", "icfr-vpn"),
      target_node_id: nominate("DiagramNodeId", "icfr-azure-ad"),
      label: "Remote access with MFA",
      color: "blue",
      anchors: [],
    },
    conn34: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-34"),
      source_node_id: nominate("DiagramNodeId", "icfr-qualys"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Vulnerability scan results",
      color: "purple",
      anchors: [],
    },
    conn35: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-35"),
      source_node_id: nominate("DiagramNodeId", "icfr-liquibase"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "DB change scripts & migrations",
      color: "purple",
      anchors: [],
    },
    conn36: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-36"),
      source_node_id: nominate("DiagramNodeId", "icfr-sharepoint-eucs"),
      target_node_id: nominate("DiagramNodeId", "icfr-oracle-epm"),
      label: "Controlled EUC uploads (FX, allocations)",
      color: "blue",
      anchors: [],
    },
    conn37: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-37"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-ad"),
      target_node_id: nominate("DiagramNodeId", "icfr-sap-s4hana"),
      label: "SSO / MFA",
      color: "green",
      anchors: [],
    },
    conn38: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-38"),
      source_node_id: nominate("DiagramNodeId", "icfr-vpn"),
      target_node_id: nominate("DiagramNodeId", "icfr-splunk-siem"),
      label: "VPN connection logs",
      color: "purple",
      anchors: [],
    },
    conn39: {
      diagram_connection_id: nominate("DiagramConnectionId", "icfr-conn-39"),
      source_node_id: nominate("DiagramNodeId", "icfr-azure-ad"),
      target_node_id: nominate("DiagramNodeId", "icfr-workday"),
      label: "SSO / MFA",
      color: "green",
      anchors: [],
    },
  },

  regions: {},
  engagement: {
    engagement_id: nominate("EngagementId", "p2p-engagement-1"),
    name: "",
    third_party: {name: "", third_party_id: nominate("ThirdPartyId", "p2p-third-party-1")},
  },
};
